import { useEffect, useState } from "react";
import { api, dateTime } from "../api.js";
import { useResource } from "./useResource.js";
import { Pager } from "./common.jsx";

// «Додаток 1. Відомості про суми ПДВ… (Д1)» → «Додаток Д1»; інша довга назва — скорочено (повна — у title)
const shortLabel = (t) => {
  const s = String(t || ""), code = s.match(/\(([^()]{1,12})\)\s*$/)?.[1];
  if (code && /^Додаток/i.test(s)) return `Додаток ${code}`;
  return s.length > 48 ? `${s.slice(0, 46).trim()}…` : s;
};
// «Усі подані» — як «Перегляд звітності» в кабінеті ДПС: документи, прийняті ДПС будь-яким шляхом (кабінет, пошта з
// M.E.Doc та інших програм, «Єдине вікно»). Під списком — вибраний документ: PDF, квитанції, додатки. Лише читання
const MONTHS = ["Січень", "Лютий", "Березень", "Квітень", "Травень", "Червень", "Липень", "Серпень", "Вересень", "Жовтень", "Листопад", "Грудень"];

export default function RegdocView({ companyId, company }) {
  const now = new Date().getFullYear();
  const [year, setYear] = useState(now), [month, setMonth] = useState(""), [page, setPage] = useState(0), [sel, setSel] = useState(null);
  const r = useResource(`/api/companies/${companyId}/regdoc?year=${year}${month ? `&month=${month}` : ""}&page=${page}`);
  const items = r.data?.items || [];
  const selected = items.find((x) => x.id === sel) || items[0] || null;
  const pages = r.data ? Math.ceil(r.data.total / (r.data.pageSize || 15)) : 0;
  return (
    <div className="card">
      <div className="erpn-filters">
        <select className="erpn-month" value={year} onChange={(e) => { setYear(Number(e.target.value)); setPage(0); setSel(null); }} aria-label="Рік">
          {Array.from({ length: 6 }, (_, i) => now - i).map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
        <select className="erpn-month" value={month} onChange={(e) => { setMonth(e.target.value); setPage(0); setSel(null); }} aria-label="Місяць">
          <option value="">Усі місяці</option>
          {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
        </select>
        <span className="muted small">Усі документи, прийняті ДПС: з кабінету, поштою з інших програм, через «Єдине вікно»</span>
      </div>
      {r.error ? <p className="error" role="alert">{r.error.message}</p> : r.pending ? <p className="muted" role="status">Читаємо кабінет…</p> : !items.length ? (
        <p className="muted">Документів за цей період немає.</p>
      ) : (
        <>
          <div className="table-wrap">
            <table className="regdoc-table">
              <thead><tr><th>Отримано ДПС</th><th>Форма</th><th>Назва</th><th>Період</th><th className="num">Додатків</th><th>Реєстр. №</th></tr></thead>
              <tbody>
                {items.map((d) => (
                  <tr key={d.id} tabIndex={0} className={selected?.id === d.id ? "selected" : ""} aria-selected={selected?.id === d.id}
                    onClick={() => setSel(d.id)} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setSel(d.id); } }}>
                    <td>{dateTime(d.received)}</td><td className="mono-sm">{d.cdoc}</td>
                    <td className="erpn-party" title={d.name}>{d.name}</td><td>{d.period}</td>
                    <td className="num">{d.appendices || "—"}</td><td className="mono-sm">{d.regNumber}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {pages > 1 && <Pager page={page} pages={pages} total={r.data.total} go={(n) => { setPage(n); setSel(null); }} />}
          {selected && <RegdocPanel key={selected.id} companyId={companyId} doc={selected} company={company} />}
        </>
      )}
    </div>
  );
}

// PDF документа просто на сторінці: рамка з нашою ж адресою (?inline=1 — API дозволяє рамку лише з цього сайту).
// Не blob: — Safari такий PDF у рамці не показує (біле вікно). Спершу перевіряємо, що PDF є (помилку кабінету
// показуємо текстом, а не JSON у рамці); вдруге браузер бере його з кешу
function PdfFrame({ url, title }) {
  const [ok, setOk] = useState(false), [err, setErr] = useState(null);
  useEffect(() => {
    let live = true;
    setOk(false); setErr(null);
    fetch(url, { credentials: "same-origin" }).then(async (r) => {
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || `Помилка ${r.status}`);
      await r.arrayBuffer();
      if (live) setOk(true);
    }).catch((e) => live && setErr(e.message));
    return () => { live = false; };
  }, [url]);
  if (err) return <p className="error small" role="alert">Не вдалося показати PDF: {err}</p>;
  if (!ok) return <p className="muted small" role="status">Завантажуємо PDF з кабінету ДПС…</p>;
  return <iframe className="pdf-frame" src={url} title={title} />;
}

// Документ із «Перегляду звітності» під списком: PDF на сторінці, перемикання на квитанції й додатки, завантаження.
// doc: { periodYear, id, cdoc?, period?, regNumber?, received?, name? } — бракуючі поля беремо з деталей документа
// Уточнити: декларацію з ПДВ (J0200…) і уточнюючий до неї (J0217…) — розрахунок J0217026; Податковий розрахунок ПДФО/ВЗ/ЄСВ
// (J0500111) — уточнюючий з рядками Д1/4ДФ, що уточнюються
const UR_KIND = (cdoc) => (/^J020(0[01]|17)/.test(cdoc) ? "vat" : /^J05001/.test(cdoc) ? "payroll" : null);
export function RegdocPanel({ companyId, doc, company }) {
  const canUr = ["preparer", "submitter", "owner"].includes(company?.role);
  const [urBusy, setUrBusy] = useState(false), [urErr, setUrErr] = useState("");
  const startUr = async (m) => {
    setUrBusy(true); setUrErr("");
    const kind = UR_KIND(String(m.cdoc || ""));
    try { const x = await api(`/api/companies/${companyId}/${kind === "vat" ? "vat-ur" : "payroll-ur"}/drafts`, { method: "POST", body: { year: Number(m.periodYear), id: String(m.id) } });
      location.hash = `#/c/${companyId}/reports/${kind === "vat" ? "ur" : "pur"}/${x.draft.id}`; }
    catch (e) { setUrErr(e.message); setUrBusy(false); }
  };
  const d = useResource(`/api/companies/${companyId}/regdoc/${doc.periodYear}/${doc.id}`);
  const main = { ...(d.data?.main || {}), ...Object.fromEntries(Object.entries(doc).filter(([, v]) => v != null && v !== "")) };
  const [shown, setShown] = useState(null); // що показано в рамці: null — сам документ
  const url = (x, inline) => `/api/companies/${companyId}/regdoc/${x.periodYear}/${x.id}/pdf?name=${encodeURIComponent(`${x.cdoc || "doc"}_${x.id}`)}${inline ? "&inline=1" : ""}`;
  const cur = shown || main;
  // На кнопці — коротка назва («Додаток Д1»), повна офіційна — у підказці: інакше кнопка з назвою на 200 символів виходить за екран
  const Files = ({ x, label }) => (
    <span className="erpn-files-group">
      <button type="button" className={`btn btn-sm ${cur.id === x.id ? "" : "btn-secondary"}`} aria-pressed={cur.id === x.id} title={label} onClick={() => setShown(x.id === main.id ? null : x)}>{shortLabel(label)}</button>
      <a className="btn btn-ghost btn-sm" href={url(x, false)} aria-label={`Завантажити ${label} в PDF`}>⬇ PDF</a>
    </span>
  );
  const [showApps, setShowApps] = useState(false);
  const apps = d.data?.appendices || [];
  return (
    <section className="erpn-panel regdoc-panel" aria-label={main.name || main.cdoc || "Документ"}>
      <div className="regdoc-head">
        <p className="regdoc-id"><b>{main.cdoc}</b>{main.period ? ` · ${main.period}` : ""}{main.regNumber ? ` · № ${main.regNumber}` : ""}{main.received ? ` · ${dateTime(main.received)}` : ""}</p>
        <div className="erpn-files">
          <Files x={main} label="Документ" />
          {d.pending ? <span className="muted small">квитанції…</span> : d.error ? <span className="error small">{d.error.message}</span> :
            d.data.receipts.length ? d.data.receipts.map((k) => <Files key={k.id} x={k} label={k.name || "Квитанція"} />) : <span className="muted small">квитанцій ще немає</span>}
          {apps.length > 0 && (
            <button type="button" className="btn btn-ghost btn-sm" aria-expanded={showApps} onClick={() => setShowApps((v) => !v)}>Додатки ({apps.length}) {showApps ? "▴" : "▾"}</button>
          )}
          <a className="btn btn-ghost btn-sm" href={url(cur, true)} target="_blank" rel="noopener">↗ у новій вкладці</a>
          {canUr && UR_KIND(String(main.cdoc || "")) && main.periodYear && (
            <button type="button" className="btn btn-sm" disabled={urBusy} onClick={() => startUr(main)} title="Скласти уточнюючий розрахунок до цього документа">{urBusy ? "Відкриваємо…" : "Уточнити"}</button>)}
        </div>
      </div>
      {urErr && <p className="error small" role="alert">{urErr}</p>}
      {main.name && <p className="regdoc-name muted small" title={main.name}>{main.name}{main.flagName ? ` · ${main.flagName}` : ""}</p>}
      {showApps && (
        <ul className="regdoc-apps">{apps.map((a) => (
          <li key={a.id}><span className="mono-sm">{a.cdoc}</span> <span className="regdoc-appname">{a.name}</span> <Files x={a} label="Показати" /></li>
        ))}</ul>
      )}
      <PdfFrame url={url(cur, true)} title={`${cur.cdoc || ""} ${cur.name || "PDF документа"}`} />
    </section>
  );
}

// Подані документи майстра (ПДВ, зарплата, фінзвітність): таблиця і під нею вибраний документ — як ПН у ПН/РК.
// items: [{ key, period, dget, summary, regdoc: { year, id, doc } }]
export function SubmittedDocs({ companyId, title, items, company }) {
  const list = items.filter((x) => x.regdoc?.id);
  const [sel, setSel] = useState(null);
  const selected = list.find((x) => x.key === sel) || list[0] || null;
  if (!list.length) return null;
  return (
    <section className="card submitted-docs" aria-labelledby={`sub-h-${title}`}>
      <h3 id={`sub-h-${title}`}>{title}</h3>
      <div className="table-wrap">
        <table className="regdoc-table">
          <thead><tr><th>Період</th><th>Подано</th><th>Форма</th><th>Підсумок</th></tr></thead>
          <tbody>{list.map((x) => (
            <tr key={x.key} tabIndex={0} className={selected?.key === x.key ? "selected" : ""} aria-selected={selected?.key === x.key}
              onClick={() => setSel(x.key)} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setSel(x.key); } }}>
              <td>{x.period}</td><td>{x.dget}</td><td className="mono-sm">{x.regdoc.doc}</td><td className="erpn-party">{x.summary || ""}</td>
            </tr>
          ))}</tbody>
        </table>
      </div>
      {selected && <RegdocPanel key={selected.key} companyId={companyId} company={company} doc={{ periodYear: selected.regdoc.year, id: selected.regdoc.id, cdoc: selected.regdoc.doc, period: selected.period }} />}
    </section>
  );
}
